/* eslint-disable no-console,no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { formatRoute } from 'react-router-named-routes';
import ContentLoader from 'react-content-loader';
// graphQL
import gql from 'graphql-tag';
// adopt
import { adopt } from 'react-adopt';
// our components
import { USER } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import { bulkUpdate } from 'helpers/updateMutationsHelper';
import { Query, Mutation } from 'components/common/Query';
import { USER_INFO_FRAGMENT } from 'components/User/Info';
import UserDetails from './Details';

/**
 * Mutation to delete users
 */
export const DELETE_USERS_MUTATION = gql`
  mutation($ids: [ID]!) {
    deleteAdminUser(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Mutation to block users
 */
export const BLOCK_USERS_MUTATION = gql`
  mutation($ids: [ID]!) {
    deactivateUsers(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Mutation to unBlock users
 */
export const UNBLOCK_USERS_MUTATION = gql`
  mutation($ids: [ID]!) {
    activateUsers(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Mutation to convert user in agency
 */
export const CREATE_AGENCY_FROM_USER_MUTATION = gql`
  mutation($user: ID!) {
    createAgencyFromUser(input: { user: $user }) {
      agency {
        id
        email
        fullname
        phone
        rank
        userType
      }
      errors {
        field
        messages
      }
      clientMutationId
    }
  }
`;

/**
 * Query to fetch user from api
 */
export const ADMIN_USER = gql`
  query AdminUser($id: ID!) {
    adminUser(id: $id) {
      ...fragmentUserInfo
      permissions {
        id
        name
      }
      permissionsGroups {
        id
        name
        permissions {
          id
          name
        }
      }
    }
  }
  ${USER_INFO_FRAGMENT}
`;

/**
 * @class
 * @augments Component
 * @description
 * HOC for user component.
 * @param {*} render - Render component.
 * @returns {*} UserDetail component.
 */
class UserDetailsContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      userTypename: 'AdminUserType',
    };
    this.Compose = adopt({
      mutateUserDelete: this.userDelete,
      mutateUserBlock: this.userBlock,
      mutateUserUnBlock: this.userUnBlock,
      mutateConvertUserToAgency: this.convertUserToAgency,
      query: this.user,
    });
    this.fetchMore = () => {};
  }

  /**
   * @description
   * User's query callback to compose with mutations.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  user = ({ render }) => {
    const { id } = this.props;
    return (
      <Query
        query={ADMIN_USER}
        variables={{ id }}
        onCompleted={data =>
          this.setState({ userTypename: data.adminUser.__typename })
        }
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, fetchMore }) => {
          this.fetchMore = fetchMore;
          return render({ data, loading, fetchMore });
        }}
      </Query>
    );
  };

  /**
   * @description
   * Mutation delete callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  userDelete = ({ render }) => (
    <Mutation
      mutation={DELETE_USERS_MUTATION}
      onCompleted={this.userDeleteCompleted}
      update={(cache, { data: { deleteAdminUser } }) => {
        const data = { status: 'REMOVED', __typename: 'AdminUserType' };

        bulkUpdate(
          cache,
          'AdminUserType',
          'Delete',
          'status',
          data,
          deleteAdminUser.successIds,
        );
      }}
    >
      {(handleUserDelete, { loading }) =>
        render({ handleUserDelete, loadingUserDelete: loading })
      }
    </Mutation>
  );

  /**
   * @description
   * Mutation block callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  userBlock = ({ render }) => {
    const { userTypename } = this.state;
    return (
      <Mutation
        mutation={BLOCK_USERS_MUTATION}
        update={(cache, { data: { deactivateUsers } }) => {
          const data = { isActive: false, __typename: userTypename };

          bulkUpdate(
            cache,
            userTypename,
            'Block',
            'isActive',
            data,
            deactivateUsers.successIds,
          );
        }}
      >
        {(handleUserBlock, { loading }) =>
          render({ handleUserBlock, loadingUserBlock: loading })
        }
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation block callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  userUnBlock = ({ render }) => {
    const { userTypename } = this.state;

    return (
      <Mutation
        mutation={UNBLOCK_USERS_MUTATION}
        update={(cache, { data: { activateUsers } }) => {
          const data = { isActive: true, __typename: userTypename };

          bulkUpdate(
            cache,
            userTypename,
            'UnBlock',
            'isActive',
            data,
            activateUsers.successIds,
          );
        }}
      >
        {(handleUserUnBlock, { loading }) =>
          render({ handleUserUnBlock, loadingUserUnblock: loading })
        }
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation convetUserToAgency callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  convertUserToAgency = ({ render }) => (
    <Mutation
      mutation={CREATE_AGENCY_FROM_USER_MUTATION}
      onCompleted={this.convertUserToAgencyCompleted}
    >
      {(handleConvertUserToAgency, { loading }) =>
        render({
          handleConvertUserToAgency,
          loadingConvertUserToAgency: loading,
        })
      }
    </Mutation>
  );

  /**
   * @description
   * OnComplete delete mutation.
   *
   * @param {Object} errors - Mutation result.
   */
  userDeleteCompleted = async ({ errors }) => {
    const history = this.context;
    if (!errors) {
      history.push(USER.LIST);
    }
  };

  /**
   * @description
   * OnComplete convertUserToAgency mutation.
   *
   * @param {Object} data - Mutation result.
   */
  convertUserToAgencyCompleted = async ({ createAgencyFromUser }) => {
    const { errors, agency } = createAgencyFromUser;
    const history = this.context;
    if (!errors) {
      history.push(formatRoute(USER.UPDATE, { id: agency.id }));
    }
  };

  /**
   * @description
   * Refresh the list.
   * @returns {Function} - Fetch More function.
   */
  handleRefresh = () => {
    this.fetchMore({
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  /**
   * @description
   * Render UserDetails component.
   *
   * @returns {*} UserDetails component.
   */
  render() {
    return (
      <this.Compose>
        {({
          mutateUserDelete: { handleUserDelete, loadingUserDelete },
          mutateUserBlock: { handleUserBlock, loadingUserBlock },
          mutateUserUnBlock: { handleUserUnBlock, loadingUserUnblock },
          mutateConvertUserToAgency: {
            handleConvertUserToAgency,
            loadingConvertUserToAgency,
          },
          query: { data, loading },
        }) => {
          if (!loading) {
            return (
              <UserDetails
                data={data}
                userBlock={items =>
                  handleUserBlock({ variables: { ids: items } })
                }
                userUnBlock={items =>
                  handleUserUnBlock({ variables: { ids: items } })
                }
                userDelete={items =>
                  handleUserDelete({ variables: { ids: items } })
                }
                convertUserToAgency={user =>
                  handleConvertUserToAgency({ variables: { user } })
                }
                userActionLoading={
                  loadingUserDelete ||
                  loadingUserBlock ||
                  loadingUserUnblock ||
                  loadingConvertUserToAgency
                }
                refresh={this.handleRefresh}
              />
            );
          }
          return (
            <ContentLoader
              height={871}
              width={1073}
              speed={2}
              primaryColor="#d9d9d9"
              secondaryColor="#ecebeb"
            >
              <path
                // eslint-disable-next-line max-len
                d="M764,61 L1065,61 C1069.41828,61 1073,64.581722 1073,69 L1073,121 C1073,125.418278 1069.41828,129 1065,129 L764,129 C759.581722,129 756,125.418278 756,121 L756,69 C756,64.581722 759.581722,61 764,61 Z M764,153 L1065,153 C1069.41828,153 1073,156.581722 1073,161 L1073,661 C1073,665.418278 1069.41828,669 1065,669 L764,669 C759.581722,669 756,665.418278 756,661 L756,161 C756,156.581722 759.581722,153 764,153 Z M764,685 L1065,685 C1069.41828,685 1073,688.581722 1073,693 L1073,863 C1073,867.418278 1069.41828,871 1065,871 L764,871 C759.581722,871 756,867.418278 756,863 L756,693 C756,688.581722 759.581722,685 764,685 Z M100,61 L701,61 C705.418278,61 709,64.581722 709,69 L709,176 C709,180.418278 705.418278,184 701,184 L100,184 C95.581722,184 92,180.418278 92,176 L92,69 C92,64.581722 95.581722,61 100,61 Z M100,213 L701,213 C705.418278,213 709,216.581722 709,221 L709,328 C709,332.418278 705.418278,336 701,336 L100,336 C95.581722,336 92,332.418278 92,328 L92,221 C92,216.581722 95.581722,213 100,213 Z M100,365 L701,365 C705.418278,365 709,368.581722 709,373 L709,480 C709,484.418278 705.418278,488 701,488 L100,488 C95.581722,488 92,484.418278 92,480 L92,373 C92,368.581722 95.581722,365 100,365 Z M32.5,61 C50.4492544,61 65,75.5507456 65,93.5 C65,111.449254 50.4492544,126 32.5,126 C14.5507456,126 2.19814969e-15,111.449254 0,93.5 C-2.19814969e-15,75.5507456 14.5507456,61 32.5,61 Z M32.5,213 C50.4492544,213 65,227.550746 65,245.5 C65,263.449254 50.4492544,278 32.5,278 C14.5507456,278 2.19814969e-15,263.449254 0,245.5 C-2.19814969e-15,227.550746 14.5507456,213 32.5,213 Z M32.5,365 C50.4492544,365 65,379.550746 65,397.5 C65,415.449254 50.4492544,430 32.5,430 C14.5507456,430 2.19814969e-15,415.449254 0,397.5 C-2.19814969e-15,379.550746 14.5507456,365 32.5,365 Z M19.5,0 L163.5,0 C174.269553,-1.97833472e-15 183,8.73044738 183,19.5 C183,30.2695526 174.269553,39 163.5,39 L19.5,39 C8.73044738,39 1.31888981e-15,30.2695526 0,19.5 C-1.31888981e-15,8.73044738 8.73044738,1.97833472e-15 19.5,0 Z M225.5,0 L345.5,0 C356.269553,-1.97833472e-15 365,8.73044738 365,19.5 C365,30.2695526 356.269553,39 345.5,39 L225.5,39 C214.730447,39 206,30.2695526 206,19.5 C206,8.73044738 214.730447,1.97833472e-15 225.5,0 Z"
                fill="#E5EAEC"
              />
            </ContentLoader>
          );
        }}
      </this.Compose>
    );
  }
}

/**
 * UserDetailsContainer PropTypes
 * @param id - prop to know User Id
 * @param typename - prop to know Admin User Type
 */

UserDetailsContainer.propTypes = {
  id: PropTypes.string.isRequired,
};

export default UserDetailsContainer;
